import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { AuthShell } from "@/pages/Login";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function Signup() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [form, setForm] = useState({
    name: "", email: "", password: "", phone: "", city: "",
    role: params.get("role") || "shopkeeper",
  });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/register", form);
      toast.success("Check your email for a verification code");
      nav(`/verify?email=${encodeURIComponent(form.email)}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Signup failed");
    } finally { setLoading(false); }
  };

  return (
    <AuthShell title="Create your account" subtitle="Join SELLEXR — it takes less than a minute.">
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="text-xs font-medium text-slate-600 uppercase tracking-wider">I am a...</label>
          <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
            <SelectTrigger data-testid="signup-role" className="mt-1.5"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="shopkeeper" data-testid="signup-role-shopkeeper">Shopkeeper (I want to hire)</SelectItem>
              <SelectItem value="freelancer" data-testid="signup-role-freelancer">Freelancer (I want to work)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600 uppercase tracking-wider">Full name</label>
          <Input data-testid="signup-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600 uppercase tracking-wider">Email</label>
          <Input data-testid="signup-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600 uppercase tracking-wider">Password</label>
          <Input data-testid="signup-password" type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-slate-600 uppercase tracking-wider">Phone</label>
            <Input data-testid="signup-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 uppercase tracking-wider">City</label>
            <Input data-testid="signup-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-1.5" />
          </div>
        </div>
        <Button data-testid="signup-submit" disabled={loading} className="btn-orange hover:bg-[#FF6A00] w-full py-6">
          {loading ? "Creating..." : "Create account"}
        </Button>
        <p className="text-sm text-slate-600 text-center">
          Already have an account? <Link to="/login" className="text-brand font-semibold">Log in</Link>
        </p>
        {form.role === "freelancer" && (
          <p className="text-xs text-slate-500 text-center bg-slate-50 p-3 rounded-lg">Freelancer accounts require admin approval after email verification.</p>
        )}
      </form>
    </AuthShell>
  );
}
